import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { StoryEngine } from '../services/StoryEngine';
import { StoryProgress, StoryNode, Chapter, Evidence } from '../types/story';

interface StoryState {
  currentChapter: Chapter | null;
  currentNode: StoryNode | null;
  progress: StoryProgress;
  isPlaying: boolean;
  currentDialogueIndex: number;
  discoveredEvidence: Evidence[];
  showChoices: boolean;
  showInvestigation: boolean;
  isLoading: boolean;
}

type StoryAction =
  | { type: 'LOAD_CHAPTER'; chapter: Chapter }
  | { type: 'NODE_CHANGED'; node: StoryNode }
  | { type: 'NEXT_DIALOGUE' }
  | { type: 'MAKE_CHOICE'; choiceId: string }
  | { type: 'DISCOVER_EVIDENCE'; evidenceId: string }
  | { type: 'TOGGLE_INVESTIGATION' }
  | { type: 'TOGGLE_CHOICES' }
  | { type: 'SET_PLAYING'; playing: boolean }
  | { type: 'RESET' };

const initialState: StoryState = {
  currentChapter: null,
  currentNode: null,
  progress: {
    currentChapter: '',
    currentNodeId: '',
    completedChapters: [],
    choices: {},
    evidence: {},
    notes: [],
    trust: {},
    lastSaved: ''
  },
  isPlaying: false,
  currentDialogueIndex: 0,
  discoveredEvidence: [],
  showChoices: false,
  showInvestigation: false,
  isLoading: false
};

function storyReducer(state: StoryState, action: StoryAction): StoryState {
  switch (action.type) {
    case 'LOAD_CHAPTER':
      return {
        ...state,
        currentChapter: action.chapter,
        isLoading: false
      };

    case 'NODE_CHANGED':
      return {
        ...state,
        currentNode: action.node,
        currentDialogueIndex: 0,
        showChoices: action.node.type === 'choice' && !!action.node.choices,
        showInvestigation: action.node.type === 'investigation'
      };

    case 'NEXT_DIALOGUE':
      if (!state.currentNode || !state.currentNode.dialogues) return state;

      const nextIndex = state.currentDialogueIndex + 1;
      const hasMoreDialogues = nextIndex < state.currentNode.dialogues.length;

      if (!hasMoreDialogues) {
        // 对话结束，显示选择或进入下一节点
        return {
          ...state,
          showChoices: !!state.currentNode?.choices,
          isPlaying: false
        };
      }

      return {
        ...state,
        currentDialogueIndex: nextIndex
      };

    case 'MAKE_CHOICE':
      return {
        ...state,
        showChoices: false
      };

    case 'DISCOVER_EVIDENCE':
      const evidence = state.currentNode?.evidence?.find(e => e.id === action.evidenceId);
      if (evidence && !state.discoveredEvidence.find(e => e.id === action.evidenceId)) {
        return {
          ...state,
          discoveredEvidence: [...state.discoveredEvidence, { ...evidence, discovered: true }]
        };
      }
      return state;

    case 'TOGGLE_INVESTIGATION':
      return {
        ...state,
        showInvestigation: !state.showInvestigation
      };

    case 'TOGGLE_CHOICES':
      return {
        ...state,
        showChoices: !state.showChoices
      };

    case 'SET_PLAYING':
      return {
        ...state,
        isPlaying: action.playing
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

interface StoryContextType {
  state: StoryState;
  engine: StoryEngine;
  actions: {
    startChapter: (chapterId: string) => void;
    makeChoice: (choiceId: string) => void;
    nextDialogue: () => void;
    discoverEvidence: (evidenceId: string) => void;
    toggleInvestigation: () => void;
    resetProgress: () => void;
    getChapterProgress: () => number;
    addNote: (content: string) => void;
  };
}

const StoryContext = createContext<StoryContextType | undefined>(undefined);

export function StoryProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(storyReducer, initialState);
  const engine = StoryEngine.getInstance();

  useEffect(() => {
    // 监听引擎事件
    const handleEvent = (event: string, data: any) => {
      switch (event) {
        case 'chapterLoaded':
          dispatch({ type: 'LOAD_CHAPTER', chapter: data.chapter });
          dispatch({ type: 'NODE_CHANGED', node: data.chapter?.nodes?.[data.chapter?.startNodeId ?? ''] });
          dispatch({ type: 'SET_PLAYING', playing: true });
          break;
        case 'nodeChanged':
          dispatch({ type: 'NODE_CHANGED', node: data.node });
          if (data.node.type === 'dialogue' && (data.node.dialogues?.length ?? 0) > 0) {
            dispatch({ type: 'SET_PLAYING', playing: true });
          } else if (data.node.type === 'choice') {
            dispatch({ type: 'TOGGLE_CHOICES' });
          }
          break;
        case 'evidenceDiscovered':
          dispatch({ type: 'DISCOVER_EVIDENCE', evidenceId: data.evidenceId });
          break;
      }
    };

    engine.addListener(handleEvent);
    return () => engine.removeListener(handleEvent);
  }, [engine]);

  const actions = {
    startChapter: (chapterId: string) => {
      dispatch({ type: 'SET_PLAYING', playing: false });
      dispatch({ type: 'RESET' });
      if (engine.loadChapter(chapterId)) {
        // 事件会在监听器中处理
      }
    },

    makeChoice: (choiceId: string) => {
      dispatch({ type: 'MAKE_CHOICE', choiceId });
      if (engine.makeChoice(choiceId)) {
        // 引擎会触发nodeChanged事件
      }
    },

    nextDialogue: () => {
      console.log('nextDialogue called', {
        currentNodeId: state.currentNode?.id,
        currentDialogueIndex: state.currentDialogueIndex,
        totalDialogues: state.currentNode?.dialogues?.length,
        hasNext: state.currentNode?.nextNodeId,
        type: state.currentNode?.type,
        showChoices: state.showChoices,
        choices: state.currentNode?.choices
      });

      if (state.currentNode && state.currentDialogueIndex < (state.currentNode.dialogues?.length ?? 0) - 1) {
        console.log('Moving to next dialogue');
        dispatch({ type: 'NEXT_DIALOGUE' });
      } else if (state.currentNode?.type === 'choice' && state.currentNode.choices && !state.showChoices) {
        // 对话结束，显示选择
        dispatch({ type: 'TOGGLE_CHOICES' });
      } else if (state.currentNode && state.currentNode.nextNodeId && !state.showChoices) {
        console.log('Moving to next node:', state.currentNode.nextNodeId);
        engine.nextNode();
      } else if (state.currentNode?.type === 'ending') {
        console.log('Completing chapter');
        engine.completeChapter();
      } else {
        console.log('No next action available');
      }
    },

    discoverEvidence: (evidenceId: string) => {
      if (engine.discoverEvidence(evidenceId)) {
        dispatch({ type: 'DISCOVER_EVIDENCE', evidenceId });
      }
    },

    toggleInvestigation: () => {
      dispatch({ type: 'TOGGLE_INVESTIGATION' });
    },

    resetProgress: () => {
      engine.resetProgress();
      dispatch({ type: 'RESET' });
    },

    getChapterProgress: () => {
      return engine.getChapterProgress();
    },

    addNote: (content: string) => {
      engine.addNote(content);
    }
  };

  return (
    <StoryContext.Provider value={{ state, engine, actions }}>
      {children}
    </StoryContext.Provider>
  );
}

export function useStory() {
  const context = useContext(StoryContext);
  if (!context) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return context;
}